import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { PageHeader, FilterBar, FilterSpacer } from '../../components/common/pageKit';
import ExportToolbar from '../../components/finance/ExportToolbar';
import { exportToPDF } from '../../utils/exportUtils';
import salesAnalyticsService from '../../services/salesAnalyticsService';
import KpiGrid from '../../components/sales/analytics/KpiGrid';
import RevenueTrendCard from '../../components/sales/analytics/RevenueTrendCard';
import FunnelSection from '../../components/sales/analytics/FunnelSection';
import PipelineSection from '../../components/sales/analytics/PipelineSection';
import RepLeaderboard from '../../components/sales/analytics/RepLeaderboard';
import MarginSection from '../../components/sales/analytics/MarginSection';
import CohortsSection from '../../components/sales/analytics/CohortsSection';
import AtRiskSection from '../../components/sales/analytics/AtRiskSection';
import TargetsSection from '../../components/sales/analytics/TargetsSection';
import ComplianceReportsSection from '../../components/sales/analytics/ComplianceReportsSection';
import { getCurrentFYRange, getDefaultComparePeriod } from './salesReportsHelpers';
import {
  SalesOverview,
  SalesTrend,
  SalesFunnel,
  SalesPipeline,
  SalesReps,
  SalesMargin,
  SalesCohorts,
  AtRiskDeals,
  SalesTarget,
  TargetInput,
} from '../../types/salesAnalytics';

const KPI_EXPORT_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  grossMarginPct: 'Gross Margin %',
  winRatePct: 'Win Rate %',
  avgDealSize: 'Avg Deal Size',
  avgSalesCycleDays: 'Avg Sales Cycle (days)',
  quotaAttainmentPct: 'Quota Attainment %',
  pipelineCoverageRatio: 'Pipeline Coverage Ratio',
};

const dash = (v: number | null | undefined): number | string => (v === null || v === undefined ? '—' : v);

/**
 * Sales Reports — best-in-market analytics on top of erp-api's
 * /sales-analytics/* endpoints (see erp-api/src/models/SalesAnalytics.js).
 *
 * The one rule every section below is built to respect: a null from the API
 * is a distinct claim from a zero, and is never silently coerced into one —
 * see each section component's own header comment for how that plays out
 * there (funnel cohort-vs-volume, margin coverage, KPI null reasons, etc).
 */
const SalesReportsPage: React.FC = () => {
  const fy = getCurrentFYRange();
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);
  const [comparing, setComparing] = useState(false);
  const [compareFrom, setCompareFrom] = useState('');
  const [compareTo, setCompareTo] = useState('');
  const [trendDays, setTrendDays] = useState(90);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [trend, setTrend] = useState<SalesTrend | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [funnel, setFunnel] = useState<SalesFunnel | null>(null);
  const [pipeline, setPipeline] = useState<SalesPipeline | null>(null);
  const [reps, setReps] = useState<SalesReps | null>(null);
  const [margin, setMargin] = useState<SalesMargin | null>(null);
  const [cohorts, setCohorts] = useState<SalesCohorts | null>(null);

  const [atRisk, setAtRisk] = useState<AtRiskDeals | null>(null);
  const [atRiskLoading, setAtRiskLoading] = useState(false);

  const [targets, setTargets] = useState<SalesTarget[] | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  const [savingTarget, setSavingTarget] = useState(false);

  const toggleComparing = (checked: boolean) => {
    setComparing(checked);
    if (checked && !compareFrom && !compareTo) {
      const def = getDefaultComparePeriod(from, to);
      setCompareFrom(def.from);
      setCompareTo(def.to);
    }
  };

  const fetchCore = async () => {
    setLoading(true);
    setError(null);
    const rangeParams = {
      from,
      to,
      compareFrom: comparing ? compareFrom : undefined,
      compareTo: comparing ? compareTo : undefined,
    };
    try {
      const [ov, fn, pl, rp, mg, ch] = await Promise.all([
        salesAnalyticsService.getOverview(rangeParams),
        salesAnalyticsService.getFunnel({ from, to }),
        salesAnalyticsService.getPipeline({ from, to }),
        salesAnalyticsService.getReps({ from, to }),
        salesAnalyticsService.getMargin({ from, to }),
        salesAnalyticsService.getCohorts({ from, to }),
      ]);
      setOverview(ov);
      setFunnel(fn);
      setPipeline(pl);
      setReps(rp);
      setMargin(mg);
      setCohorts(ch);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrend = async (days: number) => {
    setTrendLoading(true);
    try {
      const data = await salesAnalyticsService.getTrend({ metric: 'revenue', days });
      setTrend(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load revenue trend');
    } finally {
      setTrendLoading(false);
    }
  };

  const fetchAtRisk = async () => {
    setAtRiskLoading(true);
    try {
      const data = await salesAnalyticsService.getAtRisk();
      setAtRisk(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load at-risk deals');
    } finally {
      setAtRiskLoading(false);
    }
  };

  const fetchTargets = async () => {
    setTargetsLoading(true);
    setTargetsError(null);
    try {
      const data = await salesAnalyticsService.getTargets({});
      setTargets(data.targets);
    } catch (err: any) {
      setTargetsError(err.message || 'Failed to load sales targets');
    } finally {
      setTargetsLoading(false);
    }
  };

  useEffect(() => {
    fetchCore();
    fetchAtRisk();
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTrend(trendDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendDays]);

  const handleRefresh = () => {
    fetchCore();
    fetchTrend(trendDays);
    fetchAtRisk();
  };

  const handleCreateTarget = async (input: TargetInput) => {
    setSavingTarget(true);
    try {
      await salesAnalyticsService.createTarget(input);
      await fetchTargets();
      await fetchCore(); // quota attainment on the KPI row / leaderboard may now change
    } finally {
      setSavingTarget(false);
    }
  };

  const handleUpdateTarget = async (id: number, input: TargetInput) => {
    setSavingTarget(true);
    try {
      await salesAnalyticsService.updateTarget(id, input);
      await fetchTargets();
      await fetchCore();
    } finally {
      setSavingTarget(false);
    }
  };

  // -------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------

  const handleExportPDF = () => {
    exportToPDF('sales-analytics-report', `Sales_Analytics_${from}_to_${to}`, 'landscape');
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    if (overview) {
      const rows = (Object.keys(overview.kpis) as Array<keyof typeof overview.kpis>).map((key) => {
        const kpi = overview.kpis[key];
        return {
          Metric: KPI_EXPORT_LABELS[key] ?? key,
          Value: dash(kpi.value),
          'Previous Period': kpi.previousValue === undefined ? '' : dash(kpi.previousValue),
          'Change %': kpi.changePct === undefined ? '' : dash(kpi.changePct),
        };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Overview');
    }

    if (funnel) {
      const rows = funnel.steps.map((s) => ({
        Stage: s.step,
        Volume: s.count,
        Value: dash(s.value),
        'Cohort Count': s.cohortCount,
        'Conversion From Prev %': dash(s.conversionFromPrevPct),
        'Entered Directly': dash(s.enteredDirectly),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Funnel');
    }

    if (pipeline) {
      const rows = pipeline.byStage.map((s) => ({
        Stage: s.stage,
        Count: s.count,
        'Total Value': s.totalValue,
        'Weighted Value': s.weightedValue,
      }));
      rows.push({
        Stage: 'VELOCITY (value/day)',
        Count: pipeline.velocity.inputs.openCount,
        'Total Value': dash(pipeline.velocity.value) as any,
        'Weighted Value': '' as any,
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Pipeline');
    }

    if (reps) {
      const rows = reps.reps.map((r) => ({
        Rep: r.fullName ?? `User #${r.userId}`,
        'Won Count': r.wonCount,
        'Won Value': r.wonValue,
        'Lost Count': r.lostCount,
        'Win Rate %': dash(r.winRatePct),
        'Avg Deal Size': dash(r.avgDealSize),
        'Open Pipeline Count': r.openPipelineCount,
        'Open Pipeline Value': r.openPipelineValue,
        Quota: dash(r.quota),
        'Attainment %': dash(r.attainmentPct),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Reps');
    }

    if (margin) {
      const coverageRows = [
        { Metric: 'Total invoice lines', Value: margin.coverage.totalLineCount },
        { Metric: 'Lines with captured cost', Value: margin.coverage.includedLineCount },
        { Metric: 'Lines excluded (no cost captured)', Value: margin.coverage.excludedLineCount },
        { Metric: 'Coverage %', Value: dash(margin.coverage.coveragePct) },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(coverageRows), 'Margin Coverage');

      if (margin.byProduct.length) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(
            margin.byProduct.map((p) => ({
              Product: p.product_name,
              Revenue: p.revenue,
              Cost: p.cost,
              Margin: p.margin,
              'Margin %': dash(p.marginPct),
              Lines: p.lineCount,
            }))
          ),
          'Margin by Product'
        );
      }
      if (margin.byCustomer.length) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(
            margin.byCustomer.map((c) => ({
              Customer: c.company_name,
              Revenue: c.revenue,
              Cost: c.cost,
              Margin: c.margin,
              'Margin %': dash(c.marginPct),
              Lines: c.lineCount,
            }))
          ),
          'Margin by Customer'
        );
      }
    }

    if (cohorts && cohorts.months.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          cohorts.months.map((m) => ({
            Month: m.month,
            'New Revenue': m.newRevenue,
            'Repeat Revenue': m.repeatRevenue,
            'New Customers': m.newCustomers,
            'Repeat Customers': m.repeatCustomers,
          }))
        ),
        'Cohorts'
      );
    }

    XLSX.writeFile(wb, `Sales_Analytics_${from}_to_${to}.xlsx`);
  };

  return (
    <>
      <PageHeader
        title="Sales Reports"
        qualifier="— Analytics"
        subtitle="Revenue, pipeline, rep performance and margin for the selected period. A dash means genuinely not computable, never zero."
        actions={
          <ExportToolbar
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            showPrint={false}
            size="small"
          />
        }
      />

      <FilterBar>
        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />

        <FormControlLabel
          control={<Switch size="small" checked={comparing} onChange={(e) => toggleComparing(e.target.checked)} />}
          label={<Typography variant="body2">Compare with previous period</Typography>}
        />
        {comparing && (
          <>
            <TextField
              label="Compare From"
              type="date"
              size="small"
              value={compareFrom}
              onChange={(e) => setCompareFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Compare To"
              type="date"
              size="small"
              value={compareTo}
              onChange={(e) => setCompareTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
          </>
        )}

        <Button
          variant="contained"
          size="small"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
        >
          Apply
        </Button>

        <FilterSpacer />
        <Typography variant="caption" color="text.secondary">
          Defaults to the current Indian financial year to date · change the dates above and click Apply
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box id="sales-analytics-report">
        <KpiGrid kpis={overview?.kpis ?? null} comparing={comparing} />

        <RevenueTrendCard trend={trend} loading={trendLoading} days={trendDays} onDaysChange={setTrendDays} />

        <FunnelSection funnel={funnel} loading={loading} />

        <PipelineSection pipeline={pipeline} loading={loading} />

        <RepLeaderboard reps={reps?.reps ?? null} loading={loading} />

        <MarginSection margin={margin} loading={loading} />

        <CohortsSection cohorts={cohorts} loading={loading} />

        <AtRiskSection atRisk={atRisk} loading={atRiskLoading} />

        <TargetsSection
          targets={targets}
          reps={reps?.reps ?? null}
          loading={targetsLoading}
          saving={savingTarget}
          error={targetsError}
          onCreate={handleCreateTarget}
          onUpdate={handleUpdateTarget}
        />

        <ComplianceReportsSection from={from} to={to} />
      </Box>
    </>
  );
};

export default SalesReportsPage;
